// CustomDocName.tsx - a document name staff typed when requesting it (an "Other"
// document): italic everywhere in the staff UI. With `hint`, it also says it is
// custom: a tooltip on hover (and on focus where `focusable`), and once in the
// accessible name.
import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export const CUSTOM_DOC_NAME_HINT = "Custom document type, named by staff when requesting it";

export interface CustomDocNameProps {
  name: string;
  /** Off where the hint cannot show or something else says it (a "Custom" chip). */
  hint?: boolean;
  /** Tab stop for the hint; leave off inside a button, select or other control. */
  focusable?: boolean;
  className?: string;
}

export function CustomDocName({
  name,
  hint = true,
  focusable = false,
  className,
}: CustomDocNameProps) {
  if (!hint) return <span className={cn("italic", className)}>{name}</span>;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* The sr-only text is the one spoken hint: no aria-describedby to the tooltip too. */}
        <TooltipTrigger asChild aria-describedby={undefined}>
          <span
            tabIndex={focusable ? 0 : undefined}
            className={cn("italic focus-visible:underline focus-visible:outline-none", className)}
          >
            {name}
            <span className="sr-only"> ({CUSTOM_DOC_NAME_HINT})</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{CUSTOM_DOC_NAME_HINT}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default CustomDocName;
